import React, { useEffect, useMemo, useState } from "react";
import { Check, RotateCcw, Save, X } from "lucide-react";

const CONDITION_OPTIONS = [
  { value: "", label: "Sem marcação", color: "#FFFFFF" },
  { value: "carious", label: "Cárie", color: "#EF4444" },
  { value: "restored", label: "Restaurado", color: "#10B981" },
  { value: "fractured", label: "Fratura", color: "#F59E0B" },
];

const STATUS_OPTIONS = [
  { value: "healthy", label: "Saudável" },
  { value: "carious", label: "Cariado" },
  { value: "restored", label: "Restaurado" },
  { value: "extracted", label: "Extraído" },
  { value: "crown", label: "Coroa" },
  { value: "implant", label: "Implante" },
  { value: "root_canal", label: "Tratamento de canal" },
  { value: "fractured", label: "Fraturado" },
  { value: "prosthesis", label: "Prótese" },
];

const FACE_LABELS = {
  mesial: "Mesial",
  distal: "Distal",
  oclusal: "Oclusal",
  vestibular: "Vestibular",
  palatino: "Palatino",
  lingual: "Lingual",
};

const WHOLE_TOOTH_STATUSES = new Set([
  "extracted",
  "crown",
  "implant",
  "root_canal",
  "prosthesis",
]);

const getDentalContext = (toothNumber) => {
  const number = Number(toothNumber);
  const quadrant = Math.floor(number / 10);
  const position = number % 10;
  const upper = [1, 2, 5, 6].includes(quadrant);
  const lower = [3, 4, 7, 8].includes(quadrant);
  const rightSide = [1, 4, 5, 8].includes(quadrant);
  const anterior = position <= 3;

  return {
    quadrant,
    upper,
    lower,
    rightSide,
    anterior,
    innerFace: upper ? "palatino" : "lingual",
    // Mantém a chave histórica "oclusal" também nos dentes anteriores para
    // preservar relatórios e odontogramas já gravados.
    centerFace: "oclusal",
    mesialSide: rightSide ? "right" : "left",
  };
};

const normalizeSurfaces = (surfaces) => ({ ...(surfaces || {}) });

const FaceDiagram = ({ context, surfaces, selectedCondition, onChange }) => {
  const sideFace = (side) =>
    context.mesialSide === side ? "mesial" : "distal";
  const topFace = context.upper ? "vestibular" : context.innerFace;
  const bottomFace = context.upper ? context.innerFace : "vestibular";
  const centerFace = context.centerFace;

  const fillFor = (face) =>
    CONDITION_OPTIONS.find((item) => item.value === surfaces[face])?.color ||
    "#FFFFFF";

  const mark = (face) => {
    onChange(
      face,
      surfaces[face] === selectedCondition ? "" : selectedCondition,
    );
  };

  const common = (face) => ({
    fill: fillFor(face),
    stroke: surfaces[face] ? "#334155" : "#94A3B8",
    strokeWidth: surfaces[face] ? 2.2 : 1.5,
    className:
      "cursor-pointer transition-opacity hover:opacity-80 focus:outline-none",
    role: "button",
    tabIndex: 0,
    "aria-label": `${FACE_LABELS[face]}: ${
      CONDITION_OPTIONS.find((item) => item.value === surfaces[face])?.label ||
      "Sem marcação"
    }`,
    onClick: () => mark(face),
    onKeyDown: (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        mark(face);
      }
    },
  });

  return (
    <div className="mx-auto w-full max-w-md">
      <div className="mb-2 text-center text-sm font-semibold text-slate-700">
        {FACE_LABELS[topFace]}
      </div>
      <div className="flex items-center justify-center gap-3">
        <span className="w-20 text-right text-sm font-semibold text-slate-700">
          {FACE_LABELS[sideFace("left")]}
        </span>
        <svg
          viewBox="0 0 240 240"
          className="h-64 w-64 max-w-full"
          aria-label="Mapa interativo das faces do dente"
        >
          <polygon points="20,20 220,20 165,75 75,75" {...common(topFace)} />
          <polygon
            points="20,20 75,75 75,165 20,220"
            {...common(sideFace("left"))}
          />
          <rect
            x="75"
            y="75"
            width="90"
            height="90"
            rx="5"
            {...common(centerFace)}
          />
          <polygon
            points="220,20 220,220 165,165 165,75"
            {...common(sideFace("right"))}
          />
          <polygon
            points="20,220 75,165 165,165 220,220"
            {...common(bottomFace)}
          />
          <text
            x="120"
            y="125"
            textAnchor="middle"
            dominantBaseline="middle"
            className="pointer-events-none select-none fill-slate-700 text-[15px] font-bold"
          >
            {FACE_LABELS[centerFace]}
          </text>
        </svg>
        <span className="w-20 text-left text-sm font-semibold text-slate-700">
          {FACE_LABELS[sideFace("right")]}
        </span>
      </div>
      <div className="mt-2 text-center text-sm font-semibold text-slate-700">
        {FACE_LABELS[bottomFace]}
      </div>
    </div>
  );
};

const ToothModal = ({
  tooth,
  isOpen,
  onClose,
  onUpdate,
  getToothStatusLabel,
}) => {
  const context = useMemo(
    () => getDentalContext(tooth?.tooth_number),
    [tooth?.tooth_number],
  );
  const [selectedCondition, setSelectedCondition] = useState("carious");
  const [toothData, setToothData] = useState({
    status: "healthy",
    procedures: [],
    planned_procedures: [],
    notes: "",
    surfaces: {},
  });

  useEffect(() => {
    setToothData({
      status: tooth?.status || "healthy",
      procedures: tooth?.procedures || [],
      planned_procedures: tooth?.planned_procedures || [],
      notes: tooth?.notes || "",
      surfaces: normalizeSurfaces(
        tooth?.surfaces || tooth?.faces || {},
        getDentalContext(tooth?.tooth_number),
      ),
    });
  }, [tooth]);

  if (!isOpen || !tooth) return null;

  const statusOptions =
    typeof getToothStatusLabel === "function"
      ? STATUS_OPTIONS.map((option) => ({
          ...option,
          label: getToothStatusLabel(option.value) || option.label,
        }))
      : STATUS_OPTIONS;

  const cleanSurfaces = () =>
    Object.fromEntries(
      Object.entries(toothData.surfaces || {}).filter(([, value]) =>
        Boolean(value),
      ),
    );

  const handleSave = () => {
    onUpdate(
      tooth.tooth_number,
      {
        status: toothData.status,
        procedures: toothData.procedures,
        planned_procedures: toothData.planned_procedures,
        notes: toothData.notes.trim(),
        surfaces: cleanSurfaces(),
      },
      { autosave: true },
    );
    onClose();
  };

  const selectedSummary = Object.entries(cleanSurfaces()).map(
    ([face, value]) => {
      const condition =
        CONDITION_OPTIONS.find((item) => item.value === value)?.label || value;
      return `${FACE_LABELS[face] || face}: ${condition}`;
    },
  );

  const wholeToothSelected = WHOLE_TOOTH_STATUSES.has(toothData.status);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-5"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="max-h-[94vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-start justify-between border-b bg-white px-5 py-4 sm:px-7">
          <div>
            <h3 className="text-xl font-semibold text-slate-900">
              Dente {tooth.tooth_number}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {context.upper ? "Arcada superior" : "Arcada inferior"} ·{" "}
              {context.quadrant}º quadrante
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid gap-7 p-5 sm:p-7 lg:grid-cols-[minmax(0,1.15fr)_minmax(300px,.85fr)]">
          <section>
            <div className="mb-5">
              <h4 className="font-semibold text-slate-900">
                Condição por face
              </h4>
              <p className="mt-1 text-sm text-slate-500">
                Escolha uma condição e clique diretamente nas faces afetadas.
              </p>
            </div>

            <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {CONDITION_OPTIONS.map((condition) => (
                <button
                  key={condition.value || "none"}
                  type="button"
                  onClick={() => setSelectedCondition(condition.value)}
                  className={`flex min-h-11 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                    selectedCondition === condition.value
                      ? "border-blue-600 bg-blue-50 text-blue-800 ring-2 ring-blue-100"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <span
                    className="h-3.5 w-3.5 rounded border border-slate-300"
                    style={{ backgroundColor: condition.color }}
                  />
                  {condition.label}
                  {selectedCondition === condition.value && (
                    <Check className="h-4 w-4" />
                  )}
                </button>
              ))}
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <FaceDiagram
                context={context}
                surfaces={toothData.surfaces}
                selectedCondition={selectedCondition}
                onChange={(face, value) =>
                  setToothData((previous) => ({
                    ...previous,
                    surfaces: { ...previous.surfaces, [face]: value },
                  }))
                }
              />
            </div>

            <div className="mt-4 flex items-start justify-between gap-4 rounded-lg border border-blue-100 bg-blue-50 p-3">
              <div className="text-sm text-blue-900">
                {selectedSummary.length ? (
                  <>
                    <strong>Marcações:</strong> {selectedSummary.join(" · ")}
                  </>
                ) : (
                  "Nenhuma face marcada."
                )}
              </div>
              {selectedSummary.length > 0 && (
                <button
                  type="button"
                  onClick={() =>
                    setToothData((previous) => ({
                      ...previous,
                      surfaces: {},
                    }))
                  }
                  className="flex shrink-0 items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Limpar
                </button>
              )}
            </div>
          </section>

          <section className="space-y-5">
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-800">
                Condição do dente inteiro
              </label>
              <select
                value={toothData.status}
                onChange={(event) =>
                  setToothData((previous) => ({
                    ...previous,
                    status: event.target.value,
                  }))
                }
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-slate-500">
                Use este campo para situações que representam o dente como um
                todo. Para cáries, restaurações ou fraturas localizadas, marque
                as faces no diagrama.
              </p>
              {wholeToothSelected && selectedSummary.length > 0 && (
                <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                  A condição geral será destacada no odontograma; as marcações
                  por face continuarão registradas no histórico clínico.
                </p>
              )}
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-800">
                Anotações
              </label>
              <textarea
                value={toothData.notes}
                onChange={(event) =>
                  setToothData((previous) => ({
                    ...previous,
                    notes: event.target.value,
                  }))
                }
                rows={4}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder="Observações clínicas sobre este dente..."
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-800">
                Procedimentos realizados
              </label>
              <textarea
                value={toothData.procedures.join("\n")}
                onChange={(event) =>
                  setToothData((previous) => ({
                    ...previous,
                    procedures: event.target.value
                      .split("\n")
                      .map((item) => item.trim())
                      .filter(Boolean),
                  }))
                }
                rows={3}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder="Um procedimento por linha..."
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-800">
                Procedimentos planejados
              </label>
              <textarea
                value={toothData.planned_procedures.join("\n")}
                onChange={(event) =>
                  setToothData((previous) => ({
                    ...previous,
                    planned_procedures: event.target.value
                      .split("\n")
                      .map((item) => item.trim())
                      .filter(Boolean),
                  }))
                }
                rows={3}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder="Um procedimento por linha..."
              />
            </div>
          </section>
        </div>

        <div className="sticky bottom-0 flex justify-end gap-3 border-t bg-white px-5 py-4 sm:px-7">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-slate-100 px-5 py-2.5 font-medium text-slate-700 hover:bg-slate-200"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="flex items-center rounded-lg bg-blue-600 px-5 py-2.5 font-medium text-white hover:bg-blue-700"
          >
            <Save className="mr-2 h-4 w-4" />
            Salvar alterações
          </button>
        </div>
      </div>
    </div>
  );
};

export default ToothModal;
